import Loading from '@/assets/svg/ic-loading.svg';
import icComplete from '@/components/common/Voice/assets/ic_checked.png';
import icMic from '@/components/common/Voice/assets/ic_mic.png';
import { IeltsTheme, VoiceSize } from '@/types';
import { m } from 'framer-motion';
import Image from 'next/image';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import Ring from '@/components/common/Voice/Ring';
import useIeltsTheme from '@/components/ielts/common/hooks/useIeltsTheme';

const DEFAULT_DURATION = 10;
const radius = 45;
const emptyStroke = '#000';

function Voice({
  toggleVoice,
  loadingUpload,
  isUpdateComplete,
  isRecording,
  onStopRecorder,
  size = VoiceSize.small,
  duration = DEFAULT_DURATION,
  ieltsTheme = IeltsTheme.GAMIFICATION,
  color = '',
}: {
  toggleVoice: () => void;
  loadingUpload?: boolean;
  isUpdateComplete?: boolean;
  isRecording: boolean;
  onStopRecorder?: () => void;
  size?: VoiceSize;
  duration?: number;
  ieltsTheme?: IeltsTheme;
  color?: string;
}) {
  const circumference = Math.ceil(2 * Math.PI * radius);

  const fillPercents = Math.abs(Math.ceil(circumference / 100));

  const isGamification = ieltsTheme === IeltsTheme.GAMIFICATION;
  const { theme } = useIeltsTheme();

  const variants = {
    hidden: {
      strokeDashoffset: circumference,
      transition: {
        duration: 0,
        ease: 'easeIn',
      },
    },
    show: {
      strokeDashoffset: fillPercents,
      transition: {
        duration: duration || DEFAULT_DURATION,
        ease: 'easeIn',
      },
    },
  };

  const renderStyle = useMemo(() => {
    if (size === VoiceSize.small) {
      return {
        styleBox: 'w-[53px] h-[53px]',
        iconMic: 'w-[40px] h-[40px]',
        ringSize: 70,
        widthCircle: 65,
      };
    }
    if (size === VoiceSize.Mistake) {
      return {
        styleBox: 'w-[45px] h-[45px]',
        iconMic: 'min-w-[32px] min-h-[32px] w-[32px] h-[32px]',
        ringSize: 60,
        widthCircle: 55,
      };
    }
    return {
      styleBox: 'w-[96px] h-[97px]',
      iconMic: 'w-[74px] h-[74px]',
      ringSize: 110,
      widthCircle: 114,
    };
  }, [size]);

  return (
    <div
      aria-hidden
      className="relative flex justify-center items-center bg-transparent cursor-pointer hover:opacity-80"
      onClick={toggleVoice}
    >
      <div
        className={twMerge(
          'z-20 flex justify-center items-center absolute rounded-full',
          renderStyle.styleBox,
        )}
        style={{
          background: isGamification
            ? 'linear-gradient(180deg, #FFF 0%, #00B7FF 0.01%, #74D6FD 100%)'
            : color || theme.colorPrimary,
        }}
      >
        {loadingUpload && (
          <div className="absolute w-full h-full z-100 flex justify-center items-center bg-gray-500 bg-[#8080809c] rounded-full">
            <div
              className="w-[60%] h-[60%] text-white "
              style={{
                fill: 'rgb(255, 255, 255)',
              }}
            >
              <Loading className="text-white" />
            </div>
          </div>
        )}

        <Image
          priority
          src={isUpdateComplete ? icComplete : icMic}
          alt="flyer record"
          className={twMerge('min-w-[40px] min-h-[40px]', renderStyle.iconMic)}
        />
      </div>
      <svg
        viewBox="0 0 100 100"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        width={renderStyle.widthCircle}
        height={renderStyle.widthCircle}
        style={{
          zIndex: 2,
          background: 'white',
          borderRadius: '50%',
        }}
      >
        <circle
          cx="50"
          cy="50"
          r={radius}
          className="circle"
          strokeWidth={2}
          stroke={
            ieltsTheme === IeltsTheme.GAMIFICATION ? emptyStroke : color || theme.colorPrimary
          }
          fill="transparent"
        />
      </svg>
      <svg
        viewBox="0 0 100 100"
        width={renderStyle.widthCircle}
        height={renderStyle.widthCircle}
        style={{
          position: 'absolute',
          transform: 'rotate(-90deg)',
          overflow: 'visible',
          zIndex: 2,
          borderRadius: '50%',
        }}
      >
        <m.circle
          cx="50"
          cy="50"
          r={radius}
          onAnimationComplete={isRecording ? onStopRecorder : undefined}
          strokeWidth={4}
          fill="transparent"
          strokeDashoffset={fillPercents}
          strokeDasharray={circumference}
          variants={variants}
          animate={isRecording ? 'show' : 'hidden'}
          stroke={theme.recordCircleColor}
          style={{
            borderRadius: '50%',
            background: 'white',
          }}
        />
      </svg>

      {isRecording &&
        [1, 2, 3].map((el, i) => (
          <Ring key={el} index={i} size={renderStyle.ringSize} color={theme.recordRingColor} />
        ))}
    </div>
  );
}
export default Voice;
